import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Copy, Download, Sparkles, BookOpen, ArrowRight, MessageCircle, Calendar } from 'lucide-react';
import { Card, Button, Badge, PageWrapper, LoadingSpinner, Alert } from '../components/QuizUI';
import { usePaymentAPI, useEbookAPI } from '../hooks/useQuiz';

const WHATSAPP_URL = 'https://wa.me/33745522450?text=Salut%20Sabril%20%F0%9F%91%8B%20je%20viens%20de%20payer%20le%20Sabril%20Express%20(790%E2%82%AC)%20%E2%80%94%20on%20planifie%20mes%202%20visios%20%3F';

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('checking');
  const [accessCode, setAccessCode] = useState('');
  const [auditId, setAuditId] = useState('');
  const [copied, setCopied] = useState(false);
  const [pollCount, setPollCount] = useState(0);
  const [downloadUrl, setDownloadUrl] = useState('');
  const [downloading, setDownloading] = useState(false);
  
  const { checkPaymentStatus } = usePaymentAPI();
  
  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    
    const pollStatus = async () => {
      try {
        const data = await checkPaymentStatus(sessionId);
        
        if (data.payment_status === 'paid') {
          setStatus('success');
          setAccessCode(data.access_code || '');
          setAuditId(data.audit_id || '');
          if (data.download_url) {
            setDownloadUrl(data.download_url);
          } else {
            // Backend didn't return download_url — request it explicitly
            try {
              const API_URL = process.env.REACT_APP_BACKEND_URL;
              const tokRes = await fetch(`${API_URL}/api/ebook/download-token`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ session_id: sessionId }),
              });
              if (tokRes.ok) {
                const tokData = await tokRes.json();
                if (tokData.download_url) setDownloadUrl(tokData.download_url);
              }
            } catch (_e) { /* silent */ }
          }
        } else if (data.status === 'expired' || pollCount >= 10) {
          setStatus('error');
        } else {
          setPollCount(prev => prev + 1);
          setTimeout(pollStatus, 2000);
        }
      } catch (err) {
        console.error('Error checking payment:', err);
        if (pollCount >= 5) {
          setStatus('error');
        } else {
          setPollCount(prev => prev + 1);
          setTimeout(pollStatus, 2000);
        }
      }
    };
    
    pollStatus();
  }, [sessionId]);
  
  const handleCopyCode = () => {
    navigator.clipboard.writeText(accessCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  if (status === 'checking') {
    return (
      <PageWrapper className="min-h-screen bg-[#F8F5F0] flex items-center justify-center p-6">
        <Card className="p-10 text-center max-w-md">
          <LoadingSpinner size="lg" className="mx-auto mb-6" />
          <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#1A1A1A] mb-2">
            Vérification du paiement...
          </h2>
          <p className="text-[#5A5A5A]">
            Merci de patienter quelques secondes.
          </p>
        </Card>
      </PageWrapper>
    );
  }
  
  if (status === 'error') {
    return (
      <PageWrapper className="min-h-screen bg-[#F8F5F0] flex items-center justify-center p-6">
        <Card className="p-10 text-center max-w-md">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#FEF2F2] flex items-center justify-center">
            <span className="text-3xl">😕</span>
          </div>
          <h2 className="font-['Playfair_Display'] text-2xl font-bold text-[#1A1A1A] mb-2">
            Une erreur est survenue
          </h2>
          <p className="text-[#5A5A5A] mb-6">
            Le paiement n'a pas pu être confirmé. Si tu as été débité, contacte-nous.
          </p>
          <Button variant="primary" onClick={() => navigate('/')} data-testid="back-home-btn">
            Retour à l'accueil
          </Button>
        </Card>
      </PageWrapper>
    );
  }
  
  return (
    <PageWrapper className="min-h-screen bg-[#F8F5F0]">
      {/* Confetti Animation */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-3 h-3 rounded-full"
            style={{
              backgroundColor: i % 2 === 0 ? '#B8860B' : '#2D5A27',
              left: `${Math.random() * 100}%`,
              top: -20
            }}
            animate={{
              y: ['0vh', '100vh'],
              rotate: [0, 360],
              opacity: [1, 0]
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              delay: Math.random() * 2,
              repeat: 0
            }}
          />
        ))}
      </div>
      
      <div className="max-w-2xl mx-auto px-6 py-16">
        {/* Success Header */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center mb-12"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#EAF4EA] flex items-center justify-center"
          >
            <Check className="w-10 h-10 text-[#2D5A27]" />
          </motion.div>
          
          <Badge variant="green" className="mb-4">
            <Sparkles className="w-4 h-4" />
            Paiement confirmé
          </Badge>
          
          <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-[#1A1A1A] mb-4">
            Bienvenue dans Sabril Express 🎯
          </h1>
          <p className="text-lg text-[#5A5A5A]">
            Ton diagnostic 90&nbsp;min (2 visios de 45&nbsp;min) est débloqué — planifie-les maintenant sur WhatsApp.
          </p>
        </motion.div>

        {/* WhatsApp CTA — priorité #1 pour Sabril Express */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="mb-8"
        >
          <Card className="p-6 md:p-8 border-2 border-[#25D366]/40 bg-gradient-to-br from-[#E8F8ED] to-[#F8F5F0]">
            <div className="text-center">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#25D366] flex items-center justify-center">
                <MessageCircle className="w-7 h-7 text-white" />
              </div>
              <span className="text-xs font-bold uppercase tracking-widest text-[#2D5A27] block mb-2">
                Étape 1 · Prochaine action
              </span>
              <h3 className="font-['Playfair_Display'] text-2xl font-bold text-[#1A1A1A] mb-2">
                Planifie tes 2 visios de 45&nbsp;min
              </h3>
              <p className="text-sm text-[#5A5A5A] mb-5">
                Envoie-moi un message WhatsApp — je te propose 3 créneaux sous 4h.
              </p>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 w-full px-6 py-4 rounded-xl bg-[#25D366] hover:bg-[#1FBA57] text-white font-bold text-base transition-colors shadow-lg shadow-[#25D366]/30"
                data-testid="whatsapp-schedule-btn"
              >
                <MessageCircle className="w-5 h-5" />
                M'écrire sur WhatsApp — +33&nbsp;7&nbsp;45&nbsp;52&nbsp;24&nbsp;50
                <ArrowRight className="w-5 h-5" />
              </a>
              <p className="mt-3 text-xs text-[#7A7A7A]">
                <Calendar className="inline w-3 h-3 mr-1" />
                Créneaux du lundi au vendredi · 9h–19h (heure de Paris)
              </p>
            </div>
          </Card>
        </motion.div>
        
        {/* Access Code Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="p-8 mb-8">
            <div className="text-center">
              <span className="text-xs font-bold uppercase tracking-widest text-[#B8860B] block mb-3">
                Ton code d'accès
              </span>
              
              <div className="flex items-center justify-center gap-3 p-4 bg-[#F8F5F0] rounded-xl border-2 border-dashed border-[#B8860B]/30 mb-4">
                <span className="font-mono text-2xl md:text-3xl font-bold text-[#1A1A1A] tracking-wider" data-testid="access-code">
                  {accessCode}
                </span>
                <button
                  onClick={handleCopyCode}
                  className="p-2 hover:bg-[#E7E2DB] rounded-lg transition-colors"
                  data-testid="copy-code-btn"
                >
                  {copied ? (
                    <Check className="w-5 h-5 text-[#2D5A27]" />
                  ) : (
                    <Copy className="w-5 h-5 text-[#5A5A5A]" />
                  )}
                </button>
              </div>
              
              <p className="text-sm text-[#5A5A5A]">
                Conserve ce code précieusement — il te donne un accès illimité à ton ebook.
              </p>
            </div>
          </Card>
        </motion.div>
        
        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="space-y-4"
        >
          <Button 
            variant="gold" 
            size="lg" 
            className="w-full"
            disabled={!downloadUrl || downloading}
            onClick={() => {
              if (!downloadUrl) return;
              setDownloading(true);
              // Backend returns a relative URL — join with current origin
              const target = downloadUrl.startsWith('http')
                ? downloadUrl
                : `${window.location.origin}${downloadUrl}`;
              window.location.href = target;
              setTimeout(() => setDownloading(false), 2000);
            }}
            data-testid="download-ebook-pdf-btn"
          >
            <Download className="w-5 h-5" />
            {downloadUrl
              ? (downloading ? 'Téléchargement…' : 'Télécharger le Guide Ultime PDF')
              : 'Préparation du lien sécurisé…'}
            <ArrowRight className="w-5 h-5" />
          </Button>

          <Button 
            variant="secondary" 
            size="lg" 
            className="w-full"
            onClick={() => navigate(`/ebook?code=${accessCode}&audit=${auditId}`)}
            data-testid="access-ebook-btn"
          >
            <BookOpen className="w-5 h-5" />
            Voir la version interactive
          </Button>
          
          {auditId && (
            <Button 
              variant="secondary" 
              size="lg" 
              className="w-full"
              onClick={() => navigate(`/report/${auditId}`)}
              data-testid="back-to-report-btn"
            >
              Voir mon rapport d'audit
            </Button>
          )}
        </motion.div>
        
        {/* Info Alert */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-8"
        >
          <Alert variant="info">
            <strong>📧 Un email de confirmation</strong> t'a été envoyé avec ton code d'accès, le PDF et le lien WhatsApp pour planifier tes visios.
          </Alert>
        </motion.div>
      </div>
    </PageWrapper>
  );
};

export default PaymentSuccessPage;
